import { isUndefined } from 'lodash';
import { useCallback } from 'react';

import { useTrackSelectedComment } from '@air/analytics';
import { type Discussion } from '@air/api/types';
import { usePlayerStore } from '@air/component-player';

import { QueryParamNames } from '~/constants/search';
import { activeDiscussionIdParamSelector } from '~/store/router/selectors';
import { setQueryParams } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';

export type OnAnnotatedCommentClickProps = {
  onSwitchVersion: () => void;
  discussionId: Discussion['id'];
  timestamp?: number;
  hasAnnotation: boolean;
};

export const useOnAnnotatedCommentClick = () => {
  const store = useAirStore();
  const { trackSelectedComment } = useTrackSelectedComment();
  const videoElement = usePlayerStore((state) => state.videoElement);

  const onAnnotatedCommentClick = useCallback(
    async ({ onSwitchVersion, discussionId, timestamp, hasAnnotation }: OnAnnotatedCommentClickProps) => {
      const activeDiscussionId = activeDiscussionIdParamSelector(store.getState());

      if (videoElement && timestamp) {
        videoElement.currentTime = timestamp;
      }

      if (activeDiscussionId !== discussionId) {
        await setQueryParams({ [QueryParamNames.discussionId]: [discussionId] }, 'replace');
      } else {
        await setQueryParams({ [QueryParamNames.discussionId]: null }, 'replace');
      }

      onSwitchVersion();

      trackSelectedComment({
        location: 'discussions tab',
        has_annotation: hasAnnotation,
        has_timestamp: !isUndefined(timestamp),
      });
    },
    [store, trackSelectedComment, videoElement],
  );

  return { onAnnotatedCommentClick };
};
